import type { TFunction } from 'i18next';
import type { Coupon } from '../../../shared/utils/rewardTypes';

const isGerman = (lang: string): boolean => lang.startsWith('de');

export const formatMoney = (
  amount: number,
  currency: string,
  lang: string
): string =>
  new Intl.NumberFormat(isGerman(lang) ? 'de-DE' : 'en-GB', {
    style: 'currency',
    currency,
  }).format(amount);

// Matches the prototype: "30 Sep 2026" (EN) and "30.09.2026" (DE)
export const formatDate = (
  iso: string,
  lang: string,
  withTime = false
): string => {
  const options: Intl.DateTimeFormatOptions = isGerman(lang)
    ? { day: '2-digit', month: '2-digit', year: 'numeric' }
    : { day: 'numeric', month: 'short', year: 'numeric' };
  if (withTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }
  return new Date(iso).toLocaleString(
    isGerman(lang) ? 'de-DE' : 'en-GB',
    options
  );
};

// Avatar initials: first letter of first and last name, falling back to the email.
export const getInitials = (user?: {
  firstName?: string;
  lastName?: string;
  email?: string;
}): string => {
  const initials = [user?.firstName, user?.lastName]
    .map(name => name?.trim()[0] ?? '')
    .join('');
  return (initials || user?.email?.[0] || 'U').toUpperCase();
};

export const maskIban = (iban: string): string => {
  const clean = iban.replace(/\s+/g, '');
  return clean.length > 6
    ? `${clean.slice(0, 2)}••••${clean.slice(-2)}`
    : clean;
};

export const formatCouponValue = (coupon: Coupon, t: TFunction): string => {
  if (coupon.valueType === 'PERCENTAGE') {
    return t('rewardsPage.percentageOff', { value: coupon.value });
  }
  const symbol =
    coupon.currency === 'USD' ? '$' : coupon.currency === 'GBP' ? '£' : '€';
  return t('rewardsPage.currencyOff', { symbol, value: coupon.value });
};
